import React, { useState, useEffect } from 'react';
import { Container, Table, Form, InputGroup, Button, Row, Col, Pagination, Badge, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import PlanFormModal from '../../../../components/modals/PlanFormModal';
import { toast } from 'react-toastify';
import adminService from '../../../../services/AdminService';
import '../../../../styles/tables.css';

const Plans = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [billingCycleFilter, setBillingCycleFilter] = useState('');

  useEffect(() => {
    fetchPlans();
  }, [currentPage, rowsPerPage, statusFilter, billingCycleFilter]);

  // Debounced search effect
  useEffect(() => {
    if (searchQuery === '') return;

    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        fetchPlans();
      } else {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const fetchPlans = async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        page: currentPage,
        page_size: rowsPerPage
      };

      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      if (billingCycleFilter) {
        params.billing_cycle = billingCycleFilter;
      }

      const result = await adminService.getPlans(params);

      if (result.success) {
        setApiData(result.data.results);
        setTotalRecords(result.data.count);
        setTotalPages(result.data.totalPages);
      } else {
        setError(result.error);
        toast.error(result.error);
      }
    } catch (err) {
      setError('Failed to fetch plans');
      toast.error('Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  };

  const paginatedData = apiData;

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleBillingCycleFilterChange = (value) => {
    setBillingCycleFilter(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const exportToExcel = () => {
    const exportData = apiData.map(plan => ({
      ID: plan.id,
      Code: plan.code,
      Name: plan.name,
      Price: plan.price,
      'Billing Cycle': plan.billing_cycle,
      'Duration (Days)': plan.duration_days,
      'Max Retailers': plan.max_retailers || 'Unlimited',
      'Max Monthly Turnover': plan.max_monthly_turnover || 'Unlimited',
      'Max Beat Users': plan.max_beat_users || 'Unlimited',
      'Max Support IDs': plan.max_support_ids || 'Unlimited',
      'Escalation Response (Hours)': plan.escalation_response_hours || 'N/A',
      Status: plan.status,
      'Created At': new Date(plan.created_at).toLocaleDateString()
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Plans');
    XLSX.writeFile(workbook, `plans_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleAdd = () => {
    setSelectedPlan(null);
    setModalMode('add');
    setShowModal(true);
  };

  const handleEdit = (plan) => {
    setSelectedPlan(plan);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDelete = async (plan) => {
    if (window.confirm(`Are you sure you want to delete plan "${plan.name}"?`)) {
      try {
        const result = await adminService.deletePlan(plan.id);
        if (result.success) {
          toast.success('Plan deleted successfully');
          fetchPlans();
        } else {
          toast.error(result.error);
        }
      } catch (err) {
        toast.error('Failed to delete plan');
      }
    }
  };

  const handleToggleStatus = async (plan) => {
    const newStatus = plan.status === 'active' ? 'inactive' : 'active';
    try {
      const result = await adminService.togglePlanStatus(plan.id, newStatus);
      if (result.success) {
        toast.success(`Plan ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
        fetchPlans();
      } else {
        toast.error(result.error);
      }
    } catch (err) {
      toast.error('Failed to update plan status');
    }
  };

  const handleModalSubmit = async (formData) => {
    try {
      let result;
      if (modalMode === 'add') {
        result = await adminService.createPlan(formData);
        if (result.success) {
          toast.success('Plan created successfully');
          setShowModal(false);
          fetchPlans();
        }
      } else {
        result = await adminService.updatePlan(selectedPlan.id, formData);
        if (result.success) {
          toast.success('Plan updated successfully');
          setShowModal(false);
          fetchPlans();
        }
      }

      return result;
    } catch (err) {
      toast.error('Failed to save plan');
      return { success: false, error: 'Failed to save plan' };
    }
  };

  const getStatusBadge = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'active') {
      return <Badge bg="success">Active</Badge>;
    } else {
      return <Badge bg="danger">Inactive</Badge>;
    }
  };

  const getBillingCycleBadge = (cycle) => {
    const colors = {
      monthly: 'info',
      quarterly: 'primary',
      yearly: 'warning'
    };
    return <Badge bg={colors[cycle] || 'secondary'}>{cycle}</Badge>;
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ color: '#031a6f', fontWeight: '700' }}>
          <i className="fas fa-box me-2"></i>
          Subscription Plans
        </h2>
        <div className="d-flex gap-2">
          <Button variant="success" onClick={exportToExcel}>
            <i className="bi bi-file-earmark-excel me-2"></i>
            Export Excel
          </Button>
          <Button variant="primary" onClick={handleAdd}>
            <i className="bi bi-plus-lg me-2"></i>
            Add Plan
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Row className="mb-4">
        <Col md={4}>
          <InputGroup>
            <InputGroup.Text>
              <i className="bi bi-search"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search plans..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={3}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Select
            value={billingCycleFilter}
            onChange={(e) => handleBillingCycleFilterChange(e.target.value)}
          >
            <option value="">All Billing Cycles</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </Form.Select>
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive">
        <Table striped bordered hover className="modern-table">
          <thead>
            <tr>
              <th>Actions</th>
              <th>ID</th>
              <th>Code</th>
              <th>Name</th>
              <th>Price</th>
              <th>Billing Cycle</th>
              <th>Duration</th>
              <th>Max Retailers</th>
              <th>Max Turnover</th>
              <th>Status</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <div className="mt-2">Loading plans...</div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="11" className="text-center py-4 text-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan="11" className="text-center py-4 text-muted">
                  <i className="fas fa-inbox me-2"></i>
                  No plans found
                </td>
              </tr>
            ) : (
              paginatedData.map((plan) => (
                <tr key={plan.id}>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEdit(plan)}
                        disabled={loading}
                        title="Edit"
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button
                        variant={plan.status === 'active' ? 'outline-warning' : 'outline-success'}
                        size="sm"
                        onClick={() => handleToggleStatus(plan)}
                        disabled={loading}
                        title={plan.status === 'active' ? 'Deactivate' : 'Activate'}
                      >
                        <i className={`bi ${plan.status === 'active' ? 'bi-pause-circle' : 'bi-play-circle'}`}></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(plan)}
                        disabled={loading}
                        title="Delete"
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </td>
                  <td><strong>{plan.id}</strong></td>
                  <td><code>{plan.code}</code></td>
                  <td>{plan.name}</td>
                  <td>{formatCurrency(plan.price)}</td>
                  <td>{getBillingCycleBadge(plan.billing_cycle)}</td>
                  <td>{plan.duration_days} days</td>
                  <td>{plan.max_retailers || 'Unlimited'}</td>
                  <td>{plan.max_monthly_turnover ? formatCurrency(plan.max_monthly_turnover) : 'Unlimited'}</td>
                  <td>{getStatusBadge(plan.status)}</td>
                  <td>{new Date(plan.created_at).toLocaleDateString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          <Form.Select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            style={{ width: 'auto' }}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </Form.Select>
        </div>
        <div>
          Showing {totalRecords === 0 ? 0 : ((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, totalRecords)} of {totalRecords} entries
        </div>
        <Pagination>
          <Pagination.First
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          />
          <Pagination.Prev
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          />
          {[...Array(Math.min(5, totalPages || 1))].map((_, index) => {
            const pageNum = Math.max(1, Math.min(currentPage - 2 + index, (totalPages || 1) - 4 + index));
            if (pageNum > totalPages) return null;
            return (
              <Pagination.Item
                key={pageNum}
                active={pageNum === currentPage}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </Pagination.Item>
            );
          })}
          <Pagination.Next
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages || totalPages === 0}
          />
          <Pagination.Last
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
          />
        </Pagination>
      </div>

      {/* Plan Form Modal */}
      <PlanFormModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        onSave={handleModalSubmit}
        planData={selectedPlan}
        mode={modalMode}
      />
    </Container>
  );
};

export default Plans;
